import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import '../styles/Chatbot.css';
import '../styles/Interview.css';
import './feedback.js';
import Avatar from './Avatar';
import { detectEmotion } from './AvatarProcessor';
import WebcamComponent from './WebcamComponent';
import { isUserLoggedIn, getAuthToken, setAuthToken } from '../utils/auth';
import config from '../config/config';

const API_URL = process.env.NODE_ENV === 'development' 
  ? 'http://localhost:8080/api'
  : `${config.apiUrl}/api`;

const Chatbot = () => {
  const navigate = useNavigate();
  const [messages, setMessages] = useState([
    { 
      text: "Hello! I'm Zach, your case interview coach. How can I help you today?",
      sender: 'bot',
      timestamp: new Date().toISOString()
    }
  ]);
  const [input, setInput] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const [currentMood, setCurrentMood] = useState('neutral');
  const [isLoading, setIsLoading] = useState(true);
  const [isRecording, setIsRecording] = useState(false);
  const [interviewParams, setInterviewParams] = useState(null);
  const [timer, setTimer] = useState(0);
  const [isTimerRunning, setIsTimerRunning] = useState(false);
  const [isSpeaking, setIsSpeaking] = useState(false);
  const [feedback, setFeedback] = useState(null);
  const [isEnding, setIsEnding] = useState(false);
  const [isBlurred, setIsBlurred] = useState(false);
  const messagesEndRef = useRef(null);
  const [messageCount, setMessageCount] = useState(0);
  const [showLoginDialog, setShowLoginDialog] = useState(false);
  const [speechRate, setSpeechRate] = useState(1.0);

  // Check authentication on component mount and token changes
  useEffect(() => {
    const token = getAuthToken();
    if (!token) {
      navigate('/chatbot');
      return;
    }

    const userMessage = {
      text: input,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
  
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
  
    const newCount = messageCount + 1;
    setMessageCount(newCount);

    // Verify token is valid
    const verifyToken = async () => {
      try {
        await axios.get(`${API_URL}/auth/verify-token`, {
          withCredentials: true,
          headers: {
            Authorization: `Bearer ${getAuthToken()}`
          }
        });
      } catch (error) {
        if (error.response?.status === 401) {
          setAuthToken(null); // Clear invalid token
          navigate('/chatbot');
        }
      }
    };

    verifyToken();
  }, [navigate]);

  // Format time from seconds to MM:SS
  const formatTime = (seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins.toString().padStart(2, '0')}:${secs.toString().padStart(2, '0')}`;
  };

  // Speech synthesis function
  const speakText = (text) => {
    console.log("Speaking at rate:", speechRate);
    if ('speechSynthesis' in window) {
      const utterance = new SpeechSynthesisUtterance(text);
      utterance.lang = 'en-US';
      utterance.rate = speechRate; // Slider value for speech rate
      utterance.pitch = 1.0;
      
      utterance.onstart = () => setIsSpeaking(true);
      utterance.onend = () => setIsSpeaking(false);
      
      window.speechSynthesis.cancel(); // Cancel any ongoing speech
      window.speechSynthesis.speak(utterance);
    }
  };

  // Timer effect
  useEffect(() => {
    let interval;
    if (isTimerRunning) {
      interval = setInterval(() => {
        setTimer((prevTimer) => prevTimer + 1);
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [isTimerRunning]);

  // Initialize the chat with parameters from sessionStorage
  useEffect(() => {
    const storedParams = sessionStorage.getItem('interviewParams');
    
    if (!storedParams) {
      setMessages([{
        text: 'Hello! I\'m Zach, your case interviewer. Would you like to configure a specific interview or start with something random?',
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);
      setIsLoading(false);
      return;
    }
    
    try {
      const params = JSON.parse(storedParams);
      setInterviewParams(params);
      
      const startCaseInterview = async () => {
        try {
          setIsLoading(true);  // Set loading to true immediately
          const token = getAuthToken();
          const response = await axios.post(`${API_URL}/chat/start-case`, { 
            params: params

          }, {
            withCredentials: true,
            headers: {
              Authorization: `Bearer ${token}`
            }

          });

          if (response.data && response.data.message) {
            setMessages([{
              text: response.data.message,
              sender: 'bot',
              timestamp: new Date().toISOString()
            }]);
            
            // Start speech and timer
            setTimeout(() => {
              speakText(response.data.message);
            }, 300);
            setIsTimerRunning(true);
          }
        } catch (error) {
          console.error('Error starting case interview:', error);
          setMessages([{
            text: 'I encountered an error while setting up your interview. Please try again or choose different parameters.',
            sender: 'bot',
            timestamp: new Date().toISOString()
          }]);
        } finally {
          setIsLoading(false);
        }
      };

      startCaseInterview();
    } catch (error) {
      console.error('Error parsing interview parameters:', error);
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const handleInputChange = (e) => {
    setInput(e.target.value);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!input.trim()) return;
  
    const userMessage = {
      text: input,
      sender: 'user',
      timestamp: new Date().toISOString(),
    };
  
    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsTyping(true);
  
    const newCount = messageCount + 1;
    setMessageCount(newCount);

    try {
      const loggedIn = isUserLoggedIn();
  
      if (!loggedIn && newCount > 1) {
        // Show dialog on second message if user not logged in
        setIsBlurred(!isBlurred);
        setShowLoginDialog(true);
        return;
      }
  
      const response = await axios.post(`${API_URL}/chat`, {
        message: userMessage.text,
        history: messages.map(msg => ({
          role: msg.sender === 'bot' ? 'assistant' : 'user',
          content: msg.text
        })),
        params: interviewParams
      });
  
      if (response.data?.message) {
        const botMessage = {
          text: response.data.message,
          sender: 'bot',
          timestamp: new Date().toISOString()
        };
  
        setMessages(prev => [...prev, botMessage]);
        setCurrentMood(analyzeResponse(response.data.message));
        setTimeout(() => speakText(response.data.message), 300);
      } else {
        throw new Error('No valid message received');
      }
    } catch (error) {
      console.error('Chat error:', error);
      setMessages(prev => [...prev, {
        text: 'Sorry, something went wrong. Please try again later.',
        sender: 'bot',
        timestamp: new Date().toISOString()
      }]);
    } finally {
      setIsTyping(false);
    }
  };
  
  const handleStartRecording = () => {
    if (!('webkitSpeechRecognition' in window)) {
      alert('Speech recognition is not supported in your browser. Please try Chrome.');
      return;
    }
    
    setIsRecording(true);
    const recognition = new window.webkitSpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    
    recognition.onresult = (event) => {
      const transcript = Array.from(event.results)
        .map(result => result[0].transcript)
        .join('');
      setInput(transcript);
    };
    
    recognition.onerror = (event) => {
      console.error('Speech recognition error', event.error);
      setIsRecording(false);
    };
    
    recognition.start();
    window.recognition = recognition;
  };

  const handleStopRecording = () => {
    setIsRecording(false);
    if (window.recognition) {
      window.recognition.stop();
    }
  };

  const handleEndInterview = async () => {
    if (window.confirm('Are you sure you want to end this interview?')) {
      setIsEnding(true);
      try {
        const response = await axios.post(`${API_URL}/chat/end`, {
          history: messages.map(msg => ({
            role: msg.sender === 'bot' ? 'assistant' : 'user',
            content: msg.text
          })),
          params: interviewParams
        });

        if (response.data && response.data.feedback) {
          setFeedback(response.data.feedback);
          setIsTimerRunning(false);
          
          // Clear the session storage
          sessionStorage.removeItem('interviewParams');

          navigate('/feedback', {
            state: {
              feedback: response.data.feedback
            }
          });

        } else {
          throw new Error('No feedback received');
        }
      } catch (error) {
        console.error('Error ending interview:', error);
        setIsEnding(false);
        alert('Failed to end the interview. Please try again.');
      }
    }
  };

  // In a real implementation, this would analyze messages from the AI
  const analyzeResponse = (text) => {
    return detectEmotion(text);
  };

  // Demo function to allow testing different emotions
  const selectEmotion = (emotion) => {
    setCurrentMood(emotion);
  };

  if (feedback) {
    return (
      <div className="feedback-container">
        <h2>Interview Feedback</h2>
        <div className="feedback-content">
          <p>{feedback}</p>
        </div>
        <button 
          className="button primary"
          onClick={() => {
            sessionStorage.removeItem('interviewParams'); // Clear params on returning home
            navigate('/');
          }}
        >
          Return Home
        </button>
      </div>
    );
  }

  return (
    <div className="interview-container">
      <div className="chatbot-container">
        <div className="chatbot-header">
          <h2>Zach - AI Case Interviewer</h2>
          <div className="interview-controls">
            <div className="timer">{formatTime(timer)}</div>
            <button 
              className="control-button end"
              onClick={handleEndInterview}
            >
              End Interview
            </button>
            <button 
              className={`control-button blur-toggle ${isBlurred ? 'active' : ''}`}
              onClick={() => setIsBlurred(!isBlurred)}
              disabled={isEnding}
            >
              {isBlurred ? "Show Responses" : "Hide Responses"}
            </button>
          </div>
        </div>
        
        <div className="chatbot-interface">
          <div className="zach-avatar-container">
            <Avatar mood={currentMood} speaking={isTyping} />
            <div className="avatar-info">
              <h3 className="avatar-name">Zach</h3>
              <p className="avatar-title">AI Case Interview Coach</p>
              <p className="avatar-description">
                I'll guide you through this case interview, providing questions and feedback to help you prepare for consulting interviews.
              </p>
            </div>
          </div>
          
          <div className="chatbot-messages">
          {messages.map((message, index) => (
              <div 
                key={index} 
                className={`message ${message.sender === 'user' ? 'user-message' : 'bot-message'}`}
              >
                <div
                  className={`message-content ${
                    isBlurred && message.sender === 'bot' ? 'blurred' : ''
                  }`}
                >
                  {isBlurred && message.sender === 'bot'
                    ? "Zach's response is hidden."
                    : message.text}
                </div>
                <div className="message-timestamp">
                  {new Date(message.timestamp).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}
                </div>
              </div>
            ))}
            {isTyping && (
              <div className="message bot-message">
                <div className="typing-indicator">
                  <span></span>
                  <span></span>
                  <span></span>
                </div>
              </div>
            )}
            <div ref={messagesEndRef} />
          </div>
          
          <form onSubmit={handleSubmit} className="chatbot-input">
            <input
              type="text"
              value={input}
              onChange={handleInputChange}
              placeholder="Type your message here..."
              disabled={isLoading || isEnding}
            />
            <div className="input-buttons">
              {!isRecording ? (
                <button 
                  type="button"
                  className="mic-button"
                  onClick={handleStartRecording}
                  disabled={isLoading || isEnding}
                >
                  <i className="fas fa-microphone"></i>
                </button>
              ) : (
                <button 
                  type="button"
                  className="mic-button recording"
                  onClick={handleStopRecording}
                >
                  <i className="fas fa-stop"></i>
                </button>
              )}
              <button 
                type="submit" 
                disabled={!input.trim() || isLoading || isEnding}
              >
                Send
              </button>
            </div>
          </form>
          <div className="speech-rate-slider">
          <label htmlFor="speechRate">Speech Rate: {speechRate.toFixed(2)}</label>
          <input
            id="speechRate"
            type="range"
            min="0.5"
            max="2"
            step="0.01"
            value={speechRate}
            onChange={e => setSpeechRate(Number(e.target.value))}
          />
        </div>
      </div>
    </div>
    <WebcamComponent />
      {showLoginDialog && (
  <div className="login-dialog-overlay">
    <div className="login-dialog">
      <h3>Login Required</h3>
      <p>You need to log in to continue the interview.</p>
      <p>Do you want to continue?</p>
      <button onClick={() => navigate('/login')}
        className= "relogin"
         >Login</button>
      <button className="relogin" onClick={() => navigate('/')}>Cancel</button>
      
    </div>
  </div>
)}
    </div>
  );
};

export default Chatbot; 